import React from "react";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { useLocation, useNavigate } from "react-router";

import { ProjectActions } from "./ProjectActions";
import ActionModal from "../../../components/modals/action-modal";
import useAuth from "../../../hooks/useAuth";
import ProjectInfoModal from "../../../components/project/ProjectInfoModal";
import { usePermissions } from "../../../contexts/permissions";
import { ProjectActionsProvider, useProjectActions } from "../../../contexts/projectActions";
import { CHARGES_SUMMARY_PARAMS } from "../../../constants/chargesSummaryParams";

const INFO_MODAL_PARAMS = ["info", "tab", ...CHARGES_SUMMARY_PARAMS];
const SETUP_MODAL_PARAM = "projectSetupModal";

function SetupChooserDialog({ setupRequested, hasNestedFromRoute, modalWidth, title, onClose }) {
  const { openClient, openProject, openClientOnly, openDuplicateProject } = useProjectActions();
  const hideForChild =
    hasNestedFromRoute || openClient || openProject || openClientOnly || openDuplicateProject;

  if (!setupRequested || hideForChild) {
    return null;
  }

  return (
    <ActionModal
      open
      title={title}
      maxWidth={modalWidth}
      onClose={onClose}
      disableTransition
      titleFontSize="1.6rem"
    >
      <ProjectActions />
    </ActionModal>
  );
}

function ActionsModalControls() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const { resetLocalModals } = useProjectActions();
  const queryParams = new URLSearchParams(location.search);
  const isInfoOpen = queryParams.get("info") === "true";
  const hasNestedFromRoute =
    queryParams.get("addProjectModal") === "true" || queryParams.get("selectModeModal") === "true";
  const setupRequested = queryParams.get(SETUP_MODAL_PARAM) === "true" || hasNestedFromRoute;
  const { settings } = user.company;

  const showClientButton = can("projects.write") && can("clients.write") && !settings.receiving_wizard;
  const modalWidth = showClientButton ? 600 : 500;

  const updateModalParams = ({ setupOpen, clearNested = false }) => {
    const newParams = new URLSearchParams(location.search);

    if (setupOpen) {
      newParams.set(SETUP_MODAL_PARAM, "true");
    } else {
      newParams.delete(SETUP_MODAL_PARAM);
    }

    if (clearNested) {
      newParams.delete("addProjectModal");
      newParams.delete("selectModeModal");
    }

    navigate({ search: newParams.toString() }, { replace: true });
  };

  const handleClose = () => {
    resetLocalModals();
    updateModalParams({ setupOpen: false, clearNested: true });
  };

  const handleToggle = () => {
    if (setupRequested) {
      handleClose();
      return;
    }

    updateModalParams({ setupOpen: true });
  };

  const handleToggleInfo = () => {
    const newParams = new URLSearchParams(location.search);

    if (isInfoOpen) {
      INFO_MODAL_PARAMS.forEach(param => newParams.delete(param));
    } else {
      newParams.set("info", "true");
    }

    navigate({ search: newParams.toString() });
  };

  return (
    <>
      <ButtonGroup variant="outlined" aria-label="outlined button group">
        <Tooltip
          title={
            settings.receiving_wizard
              ? `Add ${settings.filter_field2}`
              : `Add ${settings.filter_field1} or ${settings.filter_field2}`
          }
          arrow
        >
          <Button id="projectSetupModalTrigger" onClick={handleToggle}>
            <CreateNewFolderOutlinedIcon />
          </Button>
        </Tooltip>
        <Tooltip title={`${settings.filter_field2} Profile`} arrow>
          <Button id="projectInfoModalTrigger" onClick={handleToggleInfo}>
            <InfoOutlinedIcon />
          </Button>
        </Tooltip>
      </ButtonGroup>
      <SetupChooserDialog
        setupRequested={setupRequested}
        hasNestedFromRoute={hasNestedFromRoute}
        modalWidth={modalWidth}
        title={`New ${settings.filter_field2} Setup`}
        onClose={handleClose}
      />
      {isInfoOpen && <ProjectInfoModal onClose={handleToggleInfo} />}
    </>
  );
}

export default function ActionsModal() {
  return (
    <ProjectActionsProvider>
      <ActionsModalControls />
    </ProjectActionsProvider>
  );
}
